import { UploadStatus } from '@fluentsms/agentnet-web-components';
import { Box, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CloudUploadOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';

interface UploaderProps {
  listTitle?: string;
  onDeleteItem?: (file?: any) => void;
  files?: (FileWithPath | FileRejection)[];
  handleFiles?: (files: FileWithPath[]) => void;
  hideInfo?: boolean;
  acceptTypes?: Record<string, string[]> | undefined;
  disabled?: boolean;
  drawerMode?: boolean;
  showNotification?: boolean;
  /**
   * @property {number} maxSize in MegaBytes.
   * Defaults to 70 MB
   */
  maxSize?: number;
  clearAll?: boolean;
  successUploads?: string[];
  errorUploads?: string[];
  isServerUpload?: boolean;
  allFiles?: any[];
  restrictDuplicate?: boolean;
  isUploadCompleted?: boolean;
  onRejectedFiles?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      background: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
    },
    fileContainer: {},
    mr2: {
      marginRight: theme.spacing(2),
    },
    file: {
      border: `1px solid #EEE`,
      borderRadius: '6px',
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    fileError: {
      borderColor: theme.palette.error.main,
    },
    fileInfo: {
      marginLeft: theme.spacing(2),
      maxWidth: '77%',
    },
    fileDelete: {
      marginLeft: 'auto',
      padding: '5px',
    },
    fileName: {
      marginBottom: '3px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
    toolTip: {
      '&:hover': {
        cursor: 'default',
      },
    },
    dropzone: {
      border: '1px dashed #c4c4c4',
      borderRadius: '4px',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: '.3s all',
      '&:hover': {
        backgroundColor: theme.palette.background.default,
      },
    },
    disabledDropzone: {
      border: '1px dashed #c4c4c4',
      borderRadius: '4px',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: '.3s all',
    },
    dragMessage: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
    },
    mb0: {
      marginBottom: 0,
    },
    blueLink: {
      color: theme.palette.primary.main,
      padding: '0 4px',
    },
    blackLink: {
      color: theme.palette.common.black,
      padding: '0 4px',
    },
  }),
);

const Uploader = ({
  listTitle,
  onDeleteItem,
  files,
  handleFiles,
  hideInfo,
  acceptTypes = {},
  disabled,
  drawerMode,
  showNotification,
  /**
   * @property {number} maxSize in MegaBytes
   */
  maxSize,
  clearAll,
  successUploads,
  errorUploads,
  isServerUpload,
  allFiles,
  restrictDuplicate,
  isUploadCompleted,
  onRejectedFiles,
}: UploaderProps): JSX.Element => {
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();

  const [myFiles, setMyFiles] = useState<(FileWithPath | FileRejection)[]>(files && files.length ? files : []);
  const [droppedFilesLength, setDroppedFilesLength] = useState<any>({ dropAcceptedFiles: 0, dropRejectedFiles: 0 });

  const uploadedFiles = myFiles.flatMap((file) => {
    if ('file' in file) {
      if (allFiles && !allFiles.some((existingFile) => existingFile.name === file.file.name)) {
        allFiles.push(file.file);
      }
      return [file.file];
    }
    if (allFiles && !allFiles.some((existingFile) => existingFile.name === file.name)) {
      allFiles.push(file);
    }
    return [file];
  });

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      if (restrictDuplicate) {
        const existingFileNames = myFiles?.map((file) => ('file' in file ? file.file.name : file.name));
        const nonDuplicateFiles = acceptedFiles.filter((file) => !existingFileNames.includes(file.name));
        if (nonDuplicateFiles.length !== acceptedFiles.length) {
          addSnackbarMessage({
            message: `Duplicate documents are not allowed`,
            type: 'error',
          });
        }
        setMyFiles([...myFiles, ...nonDuplicateFiles, ...fileRejections]);
        handleFiles && handleFiles(nonDuplicateFiles);
      } else {
        setMyFiles([...myFiles, ...acceptedFiles, ...fileRejections]);
        handleFiles && handleFiles(acceptedFiles);
      }
      setDroppedFilesLength({
        dropAcceptedFiles: acceptedFiles.length,
        dropRejectedFiles: fileRejections.length,
      });
      if (acceptedFiles.length === 0 && fileRejections.length > 0 && typeof onRejectedFiles === 'function') {
        onRejectedFiles();
      }
    },
    [myFiles],
  );

  const hasOnlyRejectedFiles = droppedFilesLength.dropAcceptedFiles === 0 && droppedFilesLength.dropRejectedFiles > 0;

  useEffect(() => {
    if (showNotification && (isUploadCompleted || hasOnlyRejectedFiles)) {
      const totalFileCount = droppedFilesLength.dropAcceptedFiles + droppedFilesLength.dropRejectedFiles;
      const uploadedDocumentsCount = droppedFilesLength.dropAcceptedFiles;
      let title = '';
      let message = '';
      let type = '';

      if (uploadedDocumentsCount === 0) {
        // No files successfully uploaded
        title = 'Upload Failed';
        message =
          totalFileCount === 1
            ? 'The file encountered an error. Please review error details and try again.'
            : `All ${totalFileCount} files encountered errors. Please review error details and try again.`;
        type = 'error';
      } else if (totalFileCount === uploadedDocumentsCount) {
        // All files successfully uploaded
        title = 'Upload Complete';
        message =
          uploadedDocumentsCount === 1
            ? '1 file successfully uploaded and is being processed.'
            : `All ${uploadedDocumentsCount} files successfully uploaded and are being processed.`;
        type = 'success';
      } else {
        // Partial upload success
        title = 'Upload Partially Complete';
        message = `${uploadedDocumentsCount} ${
          uploadedDocumentsCount === 1 ? 'file' : 'files'
        } successfully uploaded, ${totalFileCount - uploadedDocumentsCount} ${
          totalFileCount - uploadedDocumentsCount === 1 ? 'file' : 'files'
        } failed. Check failed ${totalFileCount - uploadedDocumentsCount === 1 ? 'file' : 'files'} for details.`;
        type = 'warning';
      }

      addSnackbarMessage({
        title,
        message,
        html: true,
        type,
        duration: 20000,
      });
      setDroppedFilesLength({ dropAcceptedFiles: 0, dropRejectedFiles: 0 });
    }
  }, [isUploadCompleted, showNotification, hasOnlyRejectedFiles]);

  const acceptedTypes = acceptTypes
    ? acceptTypes
    : {
        'application/pdf': ['.pdf'],
        'image/tiff': ['.tif', '.tiff'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/rtf': ['.rtf'],
      };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    disabled: disabled,
    maxSize: (maxSize ?? 70) * 1024 * 1024,
    minSize: 1,
  });

  const removeFile = (file: any) => () => {
    onDeleteItem && onDeleteItem(file);
    const newFiles = [...myFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setMyFiles(newFiles);
  };

  const removeAllFiles = () => {
    setMyFiles([]);
  };

  useEffect(() => {
    uploadedFiles;
  }, [files]);

  useEffect(() => {
    if (clearAll == true) {
      removeAllFiles();
    }
  }, [clearAll]);

  const QueuedFiles = myFiles.map((file: any, i) => {
    const hasError = file.file ? true : false;
    const isLoading = isServerUpload;
    const allFiles = successUploads?.flat();
    const isUploaded = allFiles?.some((e) => e == file.name);
    const errorUpload = errorUploads
      ?.flat()
      .flatMap((e) => e)
      .some((e) => e == file.name);
    const fileError =
      file.errors?.map((error: any) => {
        return error.code === 'file-invalid-type'
          ? 'Invalid File Type'
          : error.code === 'file-too-large'
          ? 'File exceeds size limit'
          : error.code === 'file-too-small'
          ? 'Invalid File Size'
          : 'Error';
      }) ?? ([] as string[]);
    return (
      <Grid item key={i} sm={drawerMode ? 6 : 4}>
        <UploadStatus
          file={fileError?.length > 0 ? file.file : file}
          progress={!isUploaded && isLoading && !(errorUpload || hasError) ? 75 : 100}
          onRemove={removeFile(file)}
          isLoading={!isUploaded && isLoading ? true : false}
          hideDeleteButton={isLoading && hasError ? true : false}
          hideClearButton={true}
          fullWidth={true}
          truncateFileName={false}
          errors={fileError}
          className={clsx(classes.file)}
        />
      </Grid>
    );
  });

  return (
    <Grid className={classes.root} container>
      <div {...getRootProps({ className: !disabled ? classes.dropzone : classes.disabledDropzone })}>
        <input {...getInputProps()} />
        <Typography
          variant="body2"
          color="textPrimary"
          className={clsx(classes.dragMessage, { [classes.mb0]: hideInfo })}
        >
          <CloudUploadOutlined htmlColor="#666666" className={classes.mr2} />
          Drag &amp; Drop or <span className={!disabled ? classes.blueLink : classes.blackLink}>Click to Upload</span> a
          File
        </Typography>
        {!hideInfo && (
          <>
            <Typography variant="caption" color="textSecondary">
              Supported Doc Types: {Object.values(acceptTypes).flat().join(', ').toLocaleLowerCase()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Maximum upload file size: {maxSize} MB
            </Typography>
          </>
        )}
      </div>
      {QueuedFiles.length > 0 && (
        <>
          <Box my={3}>
            <Typography variant="h3">{listTitle ? listTitle : 'Files'}</Typography>
          </Box>
          <Grid container className={classes.fileContainer} spacing={2}>
            {QueuedFiles}
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default Uploader;
